<script>
import {
  iButton,
  iButtonStyles,
  iButtonTypes,
  iIcon,
  iInlineInput,
  iMenu,
  iMenuItem,
  iMenuTriggerAlignOptions,
  iSwitch,
} from '@inscribe/signal';
import palette from '@inscribe/signal/dist/palette';

export default {
  components: {
    iButton,
    iIcon,
    iInlineInput,
    iMenu,
    iMenuItem,
    iSwitch,
  },
  props: {
    title: {
      type: String,
      default: null,
    },
    subtitle: {
      type: String,
      default: null,
    },
    categoryName: {
      type: String,
      default: null,
    },
    actionName: {
      type: String,
      default: null,
    },
    itemName: {
      type: String,
      default: null,
    },
    buttonLabel: {
      type: String,
      default: null,
    },
    buttonDisabled: {
      type: Boolean,
      default: false,
    },
    showToggle: {
      type: Boolean,
      default: false,
    },
    toggleValue: {
      type: Boolean,
      default: false,
    },
    editable: {
      type: Boolean,
      default: true,
    },
    nameEditable: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      iButtonStyles,
      iButtonTypes,
      iMenuTriggerAlignOptions,
      palette,
      itemEnabled: null,
    };
  },
  watch: {
    toggleValue: {
      handler: function (newValue) {
        this.itemEnabled = newValue;
      },
    },
  },
  mounted() {
    this.itemEnabled = this.toggleValue;
  },
  methods: {
    onBackButtonClick() {
      this.$emit('on-back-button-click');
    },
    onNameChange(evt) {
      this.$emit('on-name-change', evt.target.value);
    },
    onButtonClick(evt) {
      this.$emit('on-button-click', evt);
    },
    onDelete(evt) {
      this.$emit('on-delete', evt);
    },
    onPublicSwitchChange(evt) {
      this.$emit('on-enabled-toggle-change', evt);
    },
  },
};
</script>

<template>
  <div class="decision-engine-nav d-flex flex-row i-px-12 i-py-10">
    <i-button
      class="link-icon i-mr-6"
      :left-icon="'medium-arrows-left'"
      :type="iButtonTypes.NEUTRAL"
      :button-style="iButtonStyles.GHOST"
      data-testid="decision-engine-nav-back-button"
      @button-click="onBackButtonClick"
    ></i-button>

    <!-- data text -->
    <div class="text d-flex flex-column flex-grow-1 i-gap-2">
      <i-inline-input
        v-if="editable && nameEditable"
        :value="itemName"
        class="name-input"
        @input="onNameChange"
      />
      <div v-else class="name-input">
        {{ itemName }}
      </div>
      <div class="bread-crumbs d-flex flex-row i-gap-2">
        <div class="category i-text-subheading-3 i-text-neutral-90">
          {{ categoryName }}
        </div>
        <i-icon
          symbol="small-chevron-right"
          :colour="palette['light-neutral-80']"
          :size="16"
        ></i-icon>
        <div class="category i-text-subheading-3 i-text-neutral-120">
          {{ actionName }}
        </div>
      </div>
    </div>

    <!-- controls -->
    <div class="controls d-flex flex-row align-items-center i-gap-8">
      <template v-if="showToggle">
        <span class="i-text-subheading-2">{{
          itemEnabled ? 'Enabled' : 'Disabled'
        }}</span>
        <i-switch
          v-model="itemEnabled"
          size="sm"
          @input="onPublicSwitchChange"
        />
      </template>

      <!-- actions menu -->
      <i-menu
        v-if="editable"
        ref="menuEl"
        :trigger-align="iMenuTriggerAlignOptions.RIGHT"
        :width="200"
      >
        <template #trigger>
          <i-button
            class="trigger-button i-radius-md"
            data-testid="document-actions-button"
            :type="iButtonTypes.NEUTRAL"
            :button-style="iButtonStyles.GHOST"
            :left-icon="'medium-more-menu-vertical'"
          />
        </template>

        <template #items>
          <!-- Action One -->
          <i-menu-item
            label="Delete"
            icon="small-delete-bin-1"
            @item-click="onDelete()"
          ></i-menu-item>
        </template>
      </i-menu>

      <!-- edit create button-->
      <i-button
        v-if="editable"
        :text="buttonLabel"
        :disabled="buttonDisabled"
        data-testid="decision-engine-nav-button"
        size="lg"
        type="primary"
        @button-click="onButtonClick()"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.decision-engine-nav {
  justify-content: space-between;

  .name-input :deep(input),
  .name-input {
    max-width: 800px;
    color: $light-neutral-120;
    font-size: 18px;
    font-weight: 600;
    line-height: 21px;
  }

  .trigger-button {
    border: 1px solid $light-neutral-70;
  }

  button,
  :deep(button) {
    height: 40px;
  }
}
</style>
